import React from "react";
import { View } from "react-native";
import FastImage from "react-native-fast-image";
import { widthPercentageToDP as wp } from "react-native-responsive-screen";
import i18n from "@microdoing/shared/i18n";
import CategoryRowWithStep from "../CategoryRowWithStep";
import CategoryProgressBar from "../CategoryProgressBar";
import selectLanguage from "app/utils/selectLanguage";
import AnimatedBox from "../../components/common/AnimatedBox";
import { Box, MainText, MainTitle } from "../../../components/common";
import Badges from "../Badges";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import {
  IGoals,
  ISkill,
  IUserCategory,
} from "@microdoing/shared/services/user/types";
import { IAchievement } from "@microdoing/shared/services/externalProfile/types";
import { useSelector } from "react-redux";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { UserProfileBlock } from "../UserProfileBlock";

const getCategoryTitle = category => {
  switch (category) {
    case "leadership":
      return i18n.t("leadership");
    case "teamCohesion":
      return i18n.t("teamCohesion");
    case "resilience":
      return i18n.t("resilience");
    case "confidence":
      return "Confiance en soi";
    case "publicSpeaking":
      return i18n.t("publicSpeaking");
    case "stress":
      return "Gestion du stress";
    case "proactivity":
      return i18n.t("proactivity");
    default:
      break;
  }
};

interface Props {
  skills: ISkill[];
  hasGoals: boolean;
  categories: IUserCategory;
  goals: IGoals;
  totalSkillsPoints: number;
}

const UserStats = ({
  skills,
  hasGoals,
  categories,
  goals,
  totalSkillsPoints,
}: Props) => {
  const language = useSelector(getLanguage);

  const renderSkills = () => {
    const ranking = skills.filter(el => el.experience);
    ranking.sort((a: ISkill, b: ISkill): number =>
      b.experience > a.experience ? 1 : -1,
    );

    return ranking.map((el, i) => {
      const { label, experience } = el;
      const level = Math.floor(experience / 5);
      const progress = (experience % 5) * 0.1;
      return (
        <CategoryRowWithStep
          progress={progress}
          level={level}
          key={i}
          categoryTitle={selectLanguage({ text: label, language })}
        />
      );
    });
  };

  const renderCategories = categories => {
    const ranking: any[] = [];
    Object.keys(categories).forEach(skill => {
      if (skill !== "stress" && skill !== "confidence") {
        ranking.push([
          skill,
          categories[skill].level,
          categories[skill].experience,
        ]);
      }
    });
    ranking.sort((a, b) => {
      // if not equal
      if (b[1] > a[1]) {
        return 1;
      }
      if (b[1] < a[1]) {
        return -1;
      }
      // if level is equal sort by experience
      if (b[2] > a[2]) {
        return 1;
      }
      if (b[2] < a[2]) {
        return -1;
      }
    });

    return ranking.map((el, i) => {
      const categoryName = el[0];
      const level = categories[categoryName].level;
      const progress = categories[categoryName].experience;
      return (
        <CategoryRowWithStep
          progress={progress}
          level={level}
          key={i}
          categoryTitle={getCategoryTitle(el[0])}
        />
      );
    });
  };

  const renderGoals = () => {
    const ranking = [];
    Object.keys(categories).forEach(skill => {
      if (skill !== "stress" && skill !== "confidence") {
        ranking.push([
          skill,
          categories[skill].level,
          categories[skill].experience,
        ]);
      }
    });
    ranking.sort((a, b) => {
      // if not equal
      if (b[1] > a[1]) {
        return 1;
      }
      if (b[1] < a[1]) {
        return -1;
      }
      // if level is equal sort by experience
      if (b[2] > a[2]) {
        return 1;
      }
      if (b[2] < a[2]) {
        return -1;
      }
    });

    return ranking.map((el, i) => {
      const categoryName = el[0];
      const experience = categories[el[0]].experience;
      const level = categories[el[0]].level;
      const totalDone = level ? experience * 10 * level * 5 : experience * 10;
      const goal = goals[categoryName];
      const progress = totalDone / (goal > 0 ? goal : 1);
      return (
        <CategoryProgressBar
          key={i}
          categoryTitle={getCategoryTitle(el[0])}
          progress={progress}
          level={level}
          goal={goal}
          totalDone={totalDone}
        />
      );
    });
  };

  return (
    <Box style={styles.main}>
      {hasGoals && (
        <Box>
          <MainTitle variant="l6-bold" marginBottom="m">
            {i18n.t("my-goals")}
          </MainTitle>
          {renderGoals()}
        </Box>
      )}
      <UserProfileBlock
        title={<MainTitle variant="l6-bold">{i18n.t("my-skills")}</MainTitle>}
        subtitle={
          <MainText marginTop="xs" variant="l3-medium" color="neutral.600">
            {i18n.t("my-accumulated-skills")}
          </MainText>
        }
        headerContent={
          <MainText variant="l4-medium" color="brand.500"></MainText>
        }
        mainContent={
          skills.length > 0 ? renderSkills() : renderCategories(categories)
        }
      />
    </Box>
  );
};

const styles = {
  main: {
    width: "100%",
  },
  box: {
    width: "90%",
    paddingHorizontal: theme.spacing.m,
  },
  linkedinLogo: {
    height: normalize(20),
    width: normalize(20),
  },
};

export default UserStats;
